import { useCallback } from 'react'

import { useTenant } from '../useTenant'

interface Resource {
  id: string
  title?: string
  parentId?: string
}

export const useResourceReorder = (resources: Resource[]) => {
  const { tenant, updateTenantSettings } = useTenant()

  const rooms = resources.filter((r) => !r.parentId)
  const currentOrder = tenant?.calendarRoomOrder ?? []
  const roomIds = currentOrder.length > 0 ? currentOrder : rooms.map((r) => r.id)

  const moveResourceUp = useCallback(
    (resourceId: string) => {
      const currentIndex = roomIds.indexOf(resourceId)
      if (currentIndex <= 0) return

      const newOrder = [...roomIds]
      ;[newOrder[currentIndex - 1], newOrder[currentIndex]] = [
        newOrder[currentIndex],
        newOrder[currentIndex - 1],
      ]

      updateTenantSettings('calendarRoomOrder', newOrder).catch((error) => {
        console.error('[Reorder] Failed to move resource up:', error)
      })
    },
    [roomIds, updateTenantSettings]
  )

  const moveResourceDown = useCallback(
    (resourceId: string) => {
      const currentIndex = roomIds.indexOf(resourceId)
      if (currentIndex === -1 || currentIndex >= roomIds.length - 1) return

      const newOrder = [...roomIds]
      ;[newOrder[currentIndex], newOrder[currentIndex + 1]] = [
        newOrder[currentIndex + 1],
        newOrder[currentIndex],
      ]

      updateTenantSettings('calendarRoomOrder', newOrder).catch((error) => {
        console.error('[Reorder] Failed to move resource down:', error)
      })
    },
    [roomIds, updateTenantSettings]
  )

  const reorderResources = useCallback(
    (newOrder: string[]) => {
      updateTenantSettings('calendarRoomOrder', newOrder).catch((error) => {
        console.error('[Reorder] Failed to set resource order:', error)
      })
    },
    [updateTenantSettings]
  )

  return {
    moveResourceUp,
    moveResourceDown,
    reorderResources,
  }
}
